import { buildReportScopePromptSection } from '@process-advisor/process-miner/consultant/aiReportScope';
// ============================================================================
// periodComparison-prompt.js
//
// Builds the AI prompt for the period-over-period comparison analysis.
// Takes two snapshots (from periodComparisonOrchestrator.js) and produces
// a focused delta analysis — not a full re-assessment of either period,
// but specifically: what changed, by how much, and what it likely means.
//
// Output JSON schema is defined at the bottom of this file.
// ============================================================================

/**
 * Extract the most meaningful summary metric row from a snapshot's
 * metricsHistory array. Prefers the most recent period row, falls back
 * to whatever is available.
 */
const extractSummaryMetrics = (snapshot) => {
  const rows = snapshot?.metricsHistory || [];
  if (rows.length === 0) return null;
  // metricsHistory is ordered newest-first; first row is most recent period
  return rows[0];
};

/**
 * Pull the top N transitions by work item volume for comparison.
 */
const topTransitions = (snapshot, n = 15) =>
  (snapshot?.transitionsJSON || []).slice(0, n);

/**
 * Pull top N variants by volume.
 */
const topVariants = (snapshot, n = 15) =>
  (snapshot?.variantsJSON || []).slice(0, n);

/**
 * Build the full structured prompt for the period comparison AI call.
 *
 * @param {object} snapshotA  - Baseline period snapshot from captureConsultantSnapshot()
 * @param {object} snapshotB  - Comparison period snapshot from captureConsultantSnapshot()
 * @param {string} issueTypeStr - e.g. "Work Type = Incident"
 * @returns {string} The complete prompt string ready to send to the AI
 */
export const buildPeriodComparisonPrompt = ({ snapshotA, snapshotB, issueTypeStr, reportScopeSummary = {} }) => {
  const reportScopeText = buildReportScopePromptSection(reportScopeSummary);
  const metricsA = extractSummaryMetrics(snapshotA);
  const metricsB = extractSummaryMetrics(snapshotB);

  const payload = {
    comparison_context: {
      issue_type:      issueTypeStr || 'Unknown work type',
      period_baseline: {
        label:      snapshotA.label,
        captured_at: snapshotA.capturedAt,
        metrics:    metricsA,
      },
      period_comparison: {
        label:      snapshotB.label,
        captured_at: snapshotB.capturedAt,
        metrics:    metricsB,
      },
    },
    baseline_transitions:   topTransitions(snapshotA),
    comparison_transitions: topTransitions(snapshotB),
    baseline_variants:      topVariants(snapshotA),
    comparison_variants:    topVariants(snapshotB),
    baseline_field_usage:   snapshotA.fieldUsage   || [],
    comparison_field_usage: snapshotB.fieldUsage   || [],
    baseline_status_model:  snapshotA.statusTypeSummary  || {},
    comparison_status_model: snapshotB.statusTypeSummary || {},
  };

  const inputText = JSON.stringify(payload, null, 2);

  return `You are an expert process consultant analysing the improvement or deterioration of a workflow between two time periods.

PERIOD LABELS
- Baseline period:   "${snapshotA.label}"
- Comparison period: "${snapshotB.label}"
- Work type: ${issueTypeStr || 'Unknown'}

YOUR TASK
Compare the two periods across every available metric dimension. Identify what improved, what degraded, what stayed the same, and provide a clear verdict on whether the process is getting better or worse overall. Be specific — name the actual transitions, variants, and metrics driving each conclusion.

GENERAL RULES
- Base every discoveryRule on the data provided. Do not fabricate metrics not present in the input.
- Express metric changes as absolute delta AND percentage change where meaningful.
- If a metric is missing in one period, note it as unavailable rather than assuming zero.
- Distinguish between operationally significant changes (>10% delta) and marginal noise (<5% delta).
- Rework rate, avg duration, and variant count are the three highest-signal metrics — always address them.
- Administrative closure delay (long transitions from a completed-style state to a closed state) should be called out if visible in the transition data.
- Use plain text only. Return JSON only. No markdown fences.

METRIC COMPARISON RULES
- For each key metric, compute: baseline_value, comparison_value, absolute_delta, percent_change, and direction (improved / degraded / unchanged / unavailable).
- Direction thresholds: >5% change = improved or degraded; ≤5% = unchanged.
- For duration and rework metrics, lower = better (improved means the value went down).
- For work_items count, higher is neutral — flag it as context only, not inherently good or bad.
- For flow_efficiency, higher = better.
- For variant count, lower = better (more standardised).

TRANSITION COMPARISON RULES
- Compare the top transitions in both periods.
- Identify: transitions that appeared in one period but not the other, transitions whose avg duration changed significantly, rework transitions whose volume changed, and the longest delay transition in each period.

VARIANT COMPARISON RULES
- Compare the most common paths in both periods.
- Identify: whether the dominant path changed, whether rework variants grew or shrank as a proportion, and whether path diversity (variant count) increased or decreased.

VERDICT RULES
- overall_verdict must be one of: Significantly Improved | Improved | Stable | Deteriorated | Significantly Deteriorated
- confidence must be one of: High | Moderate | Low
- verdict_rationale must name the 2-4 strongest signals driving the verdict.
- The verdict should weight operational metrics (rework, duration, flow efficiency) more heavily than structural metrics (variant count, activities).

IMPROVEMENT SIGNALS RULES
- List 3 to 6 specific things that got meaningfully better between the periods.
- Each must name the metric or transition, the delta, and why it matters.

DETERIORATION SIGNALS RULES
- List 3 to 6 specific things that got meaningfully worse.
- Same structure as improvement signals.
- If nothing deteriorated, return an empty array and set a flag.

STABLE SIGNALS RULES
- List 2 to 4 things that stayed effectively unchanged (within noise threshold).
- These provide useful context for what the improvement/deterioration signals mean.

HYPOTHESIS RULES
- Provide 2 to 4 hypotheses about what likely caused the observed changes.
- Hypotheses should be operationally grounded (e.g. "The reduction in WIP→Pending loops suggests triage quality improved, possibly due to better intake categorisation in the comparison period").
- Do not state speculation as fact — use language like "likely", "suggests", "may indicate".

LEADERSHIP SUMMARY RULES
- 2 to 3 sentences maximum. Executive-readable. Names the verdict and the top 2 signal drivers.
- Should stand alone as a briefing sentence if extracted from the full output.

OUTPUT FORMAT
Return exactly one JSON object:
{
  "comparison_summary": {
    "headline": "<one sentence summarising the direction of change>",
    "leadership_summary": "<2-3 executive sentences>",
    "period_baseline_label": "${snapshotA.label}",
    "period_comparison_label": "${snapshotB.label}"
  },
  "overall_verdict": {
    "verdict": "Significantly Improved | Improved | Stable | Deteriorated | Significantly Deteriorated",
    "confidence": "High | Moderate | Low",
    "verdict_rationale": "<2-4 sentences naming the strongest drivers>"
  },
  "metric_deltas": [
    {
      "metric": "<metric name>",
      "category": "duration | rework | efficiency | volume | complexity | quality",
      "baseline_value": "<formatted value with unit>",
      "comparison_value": "<formatted value with unit>",
      "absolute_delta": "<e.g. -12.3 hrs or +5.2%>",
      "percent_change": "<e.g. -18.4% or +7.1%>",
      "direction": "improved | degraded | unchanged | unavailable",
      "significance": "high | medium | low",
      "why_it_matters": "<1-2 sentences>"
    }
  ],
  "transition_changes": {
    "notable_improvements": [
      { "transition": "", "baseline_avg_hrs": "", "comparison_avg_hrs": "", "delta_hrs": "", "interpretation": "" }
    ],
    "notable_deteriorations": [
      { "transition": "", "baseline_avg_hrs": "", "comparison_avg_hrs": "", "delta_hrs": "", "interpretation": "" }
    ],
    "appeared_in_comparison": ["<transition name>"],
    "disappeared_in_comparison": ["<transition name>"],
    "rework_volume_change": "<summary of how rework transition volumes shifted>"
  },
  "variant_changes": {
    "dominant_path_stable": true,
    "dominant_path_baseline": "<path description>",
    "dominant_path_comparison": "<path description>",
    "variant_count_baseline": 0,
    "variant_count_comparison": 0,
    "rework_variant_share_baseline": "<e.g. 38%>",
    "rework_variant_share_comparison": "<e.g. 31%>",
    "summary": "<2-3 sentences on overall path diversity change>"
  },
  "improvement_signals": [
    {
      "title": "",
      "metric_or_transition": "",
      "baseline_value": "",
      "comparison_value": "",
      "delta": "",
      "why_it_matters": ""
    }
  ],
  "deterioration_signals": [
    {
      "title": "",
      "metric_or_transition": "",
      "baseline_value": "",
      "comparison_value": "",
      "delta": "",
      "why_it_matters": ""
    }
  ],
  "no_deterioration": false,
  "stable_signals": [
    { "title": "", "value": "", "interpretation": "" }
  ],
  "hypotheses": [
    {
      "title": "",
      "hypothesis": "",
      "supporting_evidence": "",
      "confidence": "High | Moderate | Low"
    }
  ],
  "data_coverage": {
    "metrics_available": true,
    "transitions_available": true,
    "variants_available": true,
    "field_usage_available": true,
    "status_model_available": true,
    "time_intelligence_available": false,
    "sample_items_available": false,
    "notes": []
  }
}

${reportScopeText}
INPUT DATA
${inputText}`;
};
